import React from 'react';
import { InputStatus } from '@wix/design-system';
import { TExpirationDate } from '../../reducers/formStateReducer';
import { FormInput } from '../FormInput/FormInput';

export interface ExpirationDateProps {
  value?: { month?: number; year?: number };
  onChange?({ month, year }: { month?: number; year?: number }): any;
  className?: string;
  label?: string;
  dataHook?: string;
  onBlur?(_e: Partial<TExpirationDate>): void;
  statusMessage?: string;
  status?: InputStatus;
}

const INVALID_CHARACTERS_REGEX = /[^0-9\/]/g;

export const ExpirationDate: React.FC<ExpirationDateProps> = ({
  value = {},
  onChange: setField,
  label,
  className,
  dataHook,
  status,
  statusMessage,
  onBlur: onBlurCallback,
}) => {
  const [valueToDisplay, setValueToDisplay] = React.useState('');

  React.useEffect(() => {
    if (!value.year || !value.month) {
      return;
    }
    const yearToDisplay = value?.year.toString().substring(2);
    const monthToDisplay = `${value?.month}`.padStart(2, '0');
    setValueToDisplay(`${monthToDisplay}/${yearToDisplay}`);
  }, [value.month, value.year]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateTyped = e.target.value.replace(INVALID_CHARACTERS_REGEX, '');
    const [typedMonth, typedYear] = dateTyped.split('/');

    if (!typedYear) {
      if (valueToDisplay.endsWith('/') && dateTyped.length === 2) {
        // delete manually inserted '/'
        setValueToDisplay(typedMonth[0]);
        return;
      }

      if (typedMonth.length === 2) {
        // manually insert '/' for months 0x/1x
        setValueToDisplay(`${typedMonth}/`);
        return;
      }

      if (
        Number(typedMonth) >= 2 &&
        Number(typedMonth) <= 9 &&
        typedMonth[0] !== '0'
      ) {
        // manually insert '0x/' for months 2-9
        setValueToDisplay(`0${typedMonth}/`);
        return;
      }

      if (typedMonth.length < 2) {
        // user has started typing month which starts with 1 or 0
        setValueToDisplay(typedMonth);
        return;
      }
    }

    if (typedMonth.length < 2) {
      // user is editing the month after inserting month and year
      setValueToDisplay(`${typedMonth}/${typedYear.substring(0, 2)}`);
      return;
    }

    // in case user copy-pasted/autofill a 4-digits length
    const yearToSet =
      typedYear?.length === 4
        ? typedYear?.substring(2)
        : typedYear?.substring(0, 2);

    setField &&
      setField({
        month: Number(typedMonth),
        year: Number(`20${yearToSet}`),
      });
  };

  const onBlur = () => {
    const [month, year] = valueToDisplay.split('/');
    const newExpirationDate = {
      month: month ? Number(month) : undefined,
      year: year ? Number(`20${year}`) : undefined,
    };

    setField && setField(newExpirationDate);
    onBlurCallback && onBlurCallback(newExpirationDate);
  };

  return (
    <FormInput
      placeholder="MM/YY"
      label={label}
      status={status}
      statusMessage={statusMessage}
      className={className ?? ''}
      value={valueToDisplay}
      onChange={onChange}
      dataHook={dataHook}
      onBlur={onBlur}
      type="tel"
    />
  );
};
